import { Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { Agreement_version, AssentStatus } from '../models/agreement_version';
import { AppSettings } from '../app.settings';

@Injectable({
    providedIn: 'root'
})
export class AgreementService {

    private _assentConfirmed: boolean | null = null;
    private _currentVersions: Agreement_version[] | null = null;

    constructor(private http: HttpClient) {}

    /**
     * Fetches current agreement versions from the public endpoint.
     * Cached for the lifetime of the app session.
     */
    getCurrentVersions(): Promise<Agreement_version[]> {
        if (this._currentVersions !== null) {
            return Promise.resolve(this._currentVersions);
        }
        return new Promise((resolve, reject) => {
            this.http.get(AppSettings.API_ENDPOINT + 'user_agreements/current_versions')
                .subscribe((response: any) => {
                    if (response && response.type === 'agreement_versions') {
                        this._currentVersions = (response.data || []).map(
                            (v: any) => new Agreement_version(v)
                        );
                        resolve(this._currentVersions);
                    } else {
                        reject(response);
                    }
                }, error => reject(error));
        });
    }

    /**
     * Checks whether the authenticated user has assented to all current agreement
     * versions. Result is cached in memory — the API is only called once per
     * app session (i.e. until the page is reloaded).
     */
    checkAssentStatus(): Promise<AssentStatus> {
        if (this._assentConfirmed === true) {
            return Promise.resolve({ all_current: true, pending: [] });
        }
        return new Promise((resolve, reject) => {
            this.http.get(AppSettings.API_ENDPOINT + 'user_agreements/assent_status')
                .subscribe((response: any) => {
                    if (response && response.type === 'assent_status') {
                        const status: AssentStatus = response.data;
                        if (status.all_current) {
                            this._assentConfirmed = true;
                        }
                        resolve(status);
                    } else {
                        reject(response);
                    }
                }, error => reject(error));
        });
    }

    /**
     * Records the user's assent to the given agreement types and marks the
     * in-memory cache as confirmed so the AuthGuard doesn't re-check.
     */
    recordAssent(agreementTypes: string[]): Promise<void> {
        return new Promise((resolve, reject) => {
            this.http.post(
                AppSettings.API_ENDPOINT + 'user_agreements/record_assent',
                JSON.stringify({ agreement_types: agreementTypes }),
                AppSettings.DEFAULT_HTTP_OPTIONS
            ).subscribe((response: any) => {
                if (response && response.type === 'assent_recorded') {
                    this._assentConfirmed = true;
                    this._currentVersions = null; // allow refresh on next load
                    resolve();
                } else {
                    reject(response);
                }
            }, error => reject(error));
        });
    }

    /** Clears the cache — call on logout so the next user gets a fresh check. */
    clearCache() {
        this._assentConfirmed = null;
        this._currentVersions = null;
    }
}
